import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { format, subMonths } from "date-fns";
import { db } from "services/firebase";
import BarChartPlot from "components/plots/BarChartPlot";
import { calculateCost, formatCost } from "utils/costCalculator";

const CHART_HEIGHT_PX = 124;

const RANGE_OPTIONS = [
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
];

// Build a continuous, ascending list of the last `count` calendar months.
function buildMonthAxis(count) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = subMonths(now, count - 1 - i);
    return { value: format(d, "yyyy-MM"), label: format(d, "MMM yyyy") };
  });
}

// Pull the Kijabe slice out of a single usage_monthly document.
// Kijabe traffic is tagged with source "Kijabe Education Module"; match
// loosely so any Kijabe-flavored source (incl. future labels) is included.
function extractKijabe(data) {
  const sources = data.sources || {};
  let interactions = 0;
  const models = {}; // model name -> { prompt, completion }

  Object.entries(sources).forEach(([key, source]) => {
    const label = source.source || key;
    if (!/kijabe/i.test(key) && !/kijabe/i.test(label)) return;
    interactions += source.calls_count || 0;
    Object.entries(source.models || {}).forEach(([modelKey, model]) => {
      const name = model.model || modelKey;
      if (!models[name]) models[name] = { prompt: 0, completion: 0 };
      models[name].prompt += model.prompt_tokens || 0;
      models[name].completion += model.completion_tokens || 0;
    });
  });

  // Unique Kijabe users for the month: WordPress users are stored as
  // `kijabe_<wpId>` in the global users map.
  const userKeys = Object.keys(data.users || {}).filter((u) =>
    u.startsWith("kijabe_"),
  );

  const cost = Object.entries(models).reduce(
    (sum, [name, t]) => sum + calculateCost(name, t.prompt, t.completion),
    0,
  );

  return { interactions, userKeys, cost };
}

const KpiStat = ({ label, value }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="h6">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const GraphKijabeUsage = () => {
  const theme = useTheme();
  const [rangeMonths, setRangeMonths] = useState(6);
  const [metric, setMetric] = useState("interactions");
  const [byMonth, setByMonth] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        // usage_monthly is one document per month, so this is at most
        // `rangeMonths` reads regardless of how much Kijabe traffic exists.
        const snapshot = await db
          .collection("usage_monthly")
          .orderBy("yearMonth", "desc")
          .limit(rangeMonths)
          .get();

        if (cancelled) return;

        const map = {};
        snapshot.forEach((doc) => {
          map[doc.id] = extractKijabe(doc.data() || {});
        });
        setByMonth(map);
      } catch (loadError) {
        if (cancelled) return;
        console.error("[GraphKijabeUsage] Failed to load usage:", loadError);
        setError(loadError.message || "Failed to load Kijabe usage.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [rangeMonths]);

  const monthAxis = useMemo(() => buildMonthAxis(rangeMonths), [rangeMonths]);

  const series = useMemo(() => {
    const interactions = [];
    const users = [];
    const uniqueUsers = new Set();
    let totalInteractions = 0;
    let totalCost = 0;

    monthAxis.forEach(({ value }) => {
      const slice = byMonth[value];
      interactions.push(slice?.interactions || 0);
      users.push(slice?.userKeys?.length || 0);
      totalInteractions += slice?.interactions || 0;
      totalCost += slice?.cost || 0;
      (slice?.userKeys || []).forEach((u) => uniqueUsers.add(u));
    });

    return {
      labels: monthAxis.map((m) => m.label),
      interactions,
      users,
      totalInteractions,
      totalCost,
      uniqueUserCount: uniqueUsers.size,
    };
  }, [monthAxis, byMonth]);

  const isUsers = metric === "users";

  const tooltipLabel = useCallback(
    (context) =>
      isUsers
        ? `${context.parsed.y} unique user${context.parsed.y === 1 ? "" : "s"}`
        : `${context.parsed.y} chat interaction${
            context.parsed.y === 1 ? "" : "s"
          }`,
    [isUsers],
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Kijabe
          </Typography>
          <Typography variant="h6">
            {isUsers ? "Unique users per month" : "Chat interactions per month"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How much the chatbots on the Kijabe site are being used.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={metric} onChange={(e) => setMetric(e.target.value)}>
              <MenuItem value="interactions">Chat interactions</MenuItem>
              <MenuItem value="users">Unique users</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={rangeMonths}
              onChange={(e) => setRangeMonths(e.target.value)}
            >
              {RANGE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
        <KpiStat
          label="Unique users in range"
          value={series.uniqueUserCount.toLocaleString()}
        />
        <KpiStat
          label="Chat interactions in range"
          value={series.totalInteractions.toLocaleString()}
        />
        <KpiStat
          label="Est. cost in range"
          value={formatCost(series.totalCost)}
        />
      </Box>

      <BarChartPlot
        labels={series.labels}
        data={isUsers ? series.users : series.interactions}
        loading={loading}
        error={error}
        height={CHART_HEIGHT_PX}
        color={isUsers ? theme.palette.secondary.main : theme.palette.primary.main}
        datasetLabel={isUsers ? "Unique users" : "Chat interactions"}
        emptyMessage="No Kijabe chatbot usage recorded for this period yet."
        tooltipLabel={tooltipLabel}
        xAxisTitle="Month"
        yAxisTitle={isUsers ? "Users" : "Interactions"}
      />
    </Paper>
  );
};

export default GraphKijabeUsage;
